import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {
  PublishableVersionStatus,
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  PUBLISHABLE_VERSION_STATUS_TO_ICON,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {withLoadingScreen} from 'util/Promises';

import {publishAnalysisTemplate} from './AnalysisTemplateActions';
import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';

export interface TemplatePublishingButtonProps {
  analysisTemplate: AnalysisTemplateGraphQL;
}

export default function PublishAnalysisTemplateDropdown({
  analysisTemplate,
}: TemplatePublishingButtonProps): JSX.Element {
  const isPublished = analysisTemplate.publishedAt != null;
  const isTemplateArchived = isArchived(analysisTemplate);
  const publishingStatus = isPublished ? PublishableVersionStatus.EFFECTIVE : PublishableVersionStatus.DRAFT;

  const iconColor = PUBLISHABLE_VERSION_STATUS_TO_COLOR[publishingStatus];
  const iconName = PUBLISHABLE_VERSION_STATUS_TO_ICON[publishingStatus];
  const opacity = isTemplateArchived ? 0.5 : 1;
  const buttonText = isPublished ? 'Published' : 'Draft';

  const button = (
    <div style={{opacity}}>
      <Button
        size={ButtonSize.MEDIUM}
        buttonStyle={ButtonStyle.SECONDARY_WHITE}
        className={css(styles.buttonContainer)}
        disabled={isTemplateArchived || isPublished}
        dataTestAttrs={{component: 'TemplatePublishingButton', element: 'button'}}
      >
        <span className={css(styles.spanContainer)} style={{color: iconColor}}>
          <BenchlingIcon
            icon={iconName}
            dataTestAttrs={{
              component: 'TemplatePublishingButton',
              element: 'BenchlingIcon',
              key: iconName,
            }}
          />
        </span>
        <span className={css(styles.spanContainer)}>{buttonText}</span>
        {!isPublished && (
          <span className={css(styles.spanContainer, styles.chevronIcon)}>
            <BenchlingIcon icon="chevron-down" />
          </span>
        )}
      </Button>
    </div>
  );

  return (
    <div>
      {isTemplateArchived || isPublished ? (
        <Tooltip
          text={
            isTemplateArchived
              ? 'This analysis template is archived.'
              : 'Published analysis templates are no longer editable'
          }
        >
          {button}
        </Tooltip>
      ) : (
        <Dropdown
          closeOnInsideClick={true}
          menu={
            <Menu dropLeft={true} width={MenuWidth.FIT_CONTENT}>
              <Option
                onSelect={async () => {
                  await withLoadingScreen(publishAnalysisTemplate(analysisTemplate));
                }}
              >
                Publish analysis template
              </Option>
            </Menu>
          }
        >
          {button}
        </Dropdown>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  spanContainer: {
    marginLeft: '0.2em',
    marginRight: '0.2em',
  },
  buttonContainer: {
    padding: '8px 12px',
    borderRadius: 16,
    border: `1px solid ${Color.GRAY_700}`,
    fontSize: 14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Color.GRAY_900,
    ':hover': {
      background: Color.GRAY_50,
    },
    ':focus': {
      background: Color.BLUE_100,
    },
    height: '32px',
  },
  chevronIcon: {
    color: Color.GRAY_900,
  },
});
